import React, { ReactNode } from 'react';
import { usePricingQuery } from '../graphql/pricing.generated';
import { getLowestPrice } from '../utils/getLowestPrice';
import { getPricingInput } from '../utils/getPricingInput';
import { PricingContext } from './PricingContext';
import type { VistaExperiment } from '@99designs/experiments';
import type { Product } from '../types';

export type PricingProviderProps = {
    products: Product[];
    experiments?: VistaExperiment[];
    children: ReactNode;
};

export function experimentsToRecord(
    experiments: VistaExperiment[] | null | undefined
): Record<string, string> {
    if (!experiments) {
        return {};
    }

    return Object.fromEntries(
        experiments
            .filter((e) => e.variationKey !== null)
            .map((e) => [e.experimentKey, e.variationKey as string])
    );
}

export const PricingProvider: React.FC<PricingProviderProps> = ({
    products,
    experiments,
    children,
}) => {
    const pricingInput = getPricingInput(products);
    const experimentsInput = experimentsToRecord(experiments);

    const {
        data,
        loading,
        error: queryError,
    } = usePricingQuery({
        variables: { input: pricingInput, experiments: experimentsInput },
    });
    const productPrices = data?.pricing?.productPrices;
    const lowestPrice = getLowestPrice(productPrices);
    const error = queryError ?? undefined;

    return (
        <PricingContext.Provider value={{ productPrices, lowestPrice, loading, error }}>
            {children}
        </PricingContext.Provider>
    );
};
